import christmasBackgroundMusicSound from '~/assets/2020-12-16_-_christmas_rock_-_www.fesliyan-studios.com_steve_oxen.ogg';
import wooshSound from '~/assets/60013__qubodup__whoosh.mp3';
import waitFinishedSound from '~/assets/376817__original_sound__impact-cinematic.ogg';
import waitForReadinessSound from '~/assets/459342__papaninkasettratat__cinematic-music-short.ogg';
import backgroundMusicSound from '~/assets/funk-cool-groove-(no-copyright-music)-by-anwar-amr.ogg';
import halloweenWaitFinishedSound from '~/assets/halloween/boo-and-laugh-7060.mp3';
import halloweenWooshSound from '~/assets/halloween/croworraven1-6749.mp3';
import halloweenBackgroundMusicSound from '~/assets/halloween/halloween-cinematic-by-infraction-[no-copyright-music]-halloween-2024-[-ezmp3.cc-].mp3';
import halloweenSelectSongSound from '~/assets/halloween/halloween-impact-05-93808.mp3';
import halloweenWaitForReadinessSound from '~/assets/halloween/scary-music-box-for-spooky-scenes-165983.mp3';
import menuBackSound from '~/assets/menu_back.mp3';
import menuEnterSound from '~/assets/menu_enter.mp3';
import menuNavigateSound from '~/assets/menu_navigate.mp3';
import { backgroundTheme } from '~/modules/elements/layout-with-background';
import {
  BackgroundThemeSetting,
  EffectsVolumeSetting,
  MasterVolumeSetting,
  MusicVolumeSetting,
} from '~/routes/settings/settings-state';

/**
 * Music is anything long and looping; effects are the short ones. They are turned down separately,
 * because a player who wants the room quiet usually still wants to hear that a keypress landed.
 */
type SoundCategory = 'music' | 'effect';

/** Every `Sound` ever built, so a volume change reaches the ones already constructed. */
const allSounds = new Set<Sound>();

const categorySetting = { music: MusicVolumeSetting, effect: EffectsVolumeSetting };

class Sound {
  private sound: HTMLAudioElement | null = null;
  constructor(
    private options: {
      src: string;
      volume?: number;
      loop?: boolean;
      preload?: boolean;
      category?: SoundCategory;
    },
  ) {
    if (global.Audio) {
      this.sound = new Audio(options.src);
      this.sound.preload = options.preload ? 'auto' : 'none';
      this.sound.loop = options.loop ?? false;
      this.sound.volume = this.effectiveVolume();
    }
    allSounds.add(this);
  }

  /**
   * The volume this sound was authored at, scaled by its category and by master. The authored value
   * is the mix - one sound being quieter than another is deliberate - so the settings scale it
   * rather than replace it.
   */
  private effectiveVolume = () => {
    const category = categorySetting[this.options.category ?? 'effect'].get() / 100;
    const master = MasterVolumeSetting.get() / 100;

    return Math.max(0, Math.min(1, (this.options.volume ?? 1) * category * master));
  };

  public applyVolume = () => {
    if (this.sound) this.sound.volume = this.effectiveVolume();
  };

  public play = async (parallel = true) => {
    try {
      if (parallel && this.playing()) {
        const clonedNode = this.sound?.cloneNode(true) as HTMLAudioElement;
        // `cloneNode` copies attributes, and volume is not one - without this the overlapping copy
        // plays at full blast however far the player has turned things down.
        clonedNode.volume = this.effectiveVolume();
        await clonedNode.play();
        clonedNode.remove();
      } else {
        if (this.sound) {
          this.sound.currentTime = 0;
          await this.sound?.play();
        }
      }
    } catch (data) {
      return console.warn(data);
    }
  };

  public pause = () => {
    this.sound?.pause();
  };

  public stop = () => {
    if (this.sound) {
      this.sound.pause();
      this.sound.currentTime = 0;
    }
  };

  public playing = () => {
    return !this.sound?.paused;
  };
}

class ThemedSound {
  constructor(
    private defaultSound: Sound,
    private themeOverrides?: Partial<Record<backgroundTheme, Sound>>,
  ) {}

  private getProperSound = () => {
    const theme = BackgroundThemeSetting.get();

    return this.themeOverrides?.[theme] ?? this.defaultSound;
  };

  public play = async (parallel = true) => this.getProperSound().play(parallel);
  public pause = () => this.getProperSound().pause();
  public stop = () => this.getProperSound().stop();
  public playing = () => this.getProperSound().playing();
}

export const menuNavigate = new Sound({
  src: menuNavigateSound,
});

export const menuEnter = new Sound({
  src: menuEnterSound,
});

export const menuBack = new Sound({
  src: menuBackSound,
});

export const woosh = new ThemedSound(
  new Sound({
    src: wooshSound,
    preload: true,
    volume: 0.5,
  }),
  {
    halloween: new Sound({
      src: halloweenWooshSound,
      preload: true,
      volume: 0.7,
    }),
  },
);

export const backgroundMusic = new ThemedSound(
  new Sound({
    src: backgroundMusicSound,
    volume: 0.3,
    loop: true,
    category: 'music',
  }),
  {
    christmas: new Sound({
      src: christmasBackgroundMusicSound,
      volume: 0.25,
      loop: true,
      category: 'music',
    }),
    halloween: new Sound({
      src: halloweenBackgroundMusicSound,
      volume: 0.3,
      loop: true,
      category: 'music',
    }),
  },
);

export const waitFinished = new ThemedSound(new Sound({ src: waitFinishedSound, preload: true }), {
  halloween: new Sound({ src: halloweenWaitFinishedSound, preload: true, volume: 0.4 }),
});
export const waitForReadinessMusic = new ThemedSound(
  new Sound({ src: waitForReadinessSound, volume: 0.8, category: 'music' }),
  { halloween: new Sound({ src: halloweenWaitForReadinessSound, volume: 0.2, category: 'music' }) },
);

export const selectHalloweenSong = new Sound({ src: halloweenSelectSongSound, preload: true, volume: 0.5 });

// Every sound is built at import time, so a change has to reach the existing instances rather than
// only the next one constructed.
const applyVolumeToAll = () => allSounds.forEach((sound) => sound.applyVolume());

MasterVolumeSetting.addListener(applyVolumeToAll);
MusicVolumeSetting.addListener(applyVolumeToAll);
EffectsVolumeSetting.addListener(applyVolumeToAll);
